import { useState, useEffect, useCallback, useRef } from "react";
import { expenseAPI, haulAPI } from "@/lib/api";
import {
  Receipt, Plus, Trash, PencilSimple, Spinner, X, Check,
  Upload, Camera, FunnelSimple, CaretDown, ArrowLeft,
} from "@phosphor-icons/react";
import BottomNav from "@/components/app/BottomNav";
import FinanceTabs from "@/components/app/FinanceTabs";
import { useConfirm } from "@/components/app/ConfirmDialog";

const CATEGORIES = [
  "Shipping Supplies", "Office Supplies", "Packaging",
  "Equipment", "Software/Subscriptions", "Gas/Mileage",
  "Sourcing Supplies", "Other",
];

export default function ExpensesPage() {
  const confirm = useConfirm();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [filterCat, setFilterCat] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [pendingReview, setPendingReview] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [hauls, setHauls] = useState([]);
  const fileRef = useRef(null);

  const loadExpenses = useCallback(async () => {
    try {
      const [expRes, haulRes] = await Promise.all([
        expenseAPI.list(200, 0, filterCat),
        haulAPI.list(),
      ]);
      setExpenses(expRes.data.expenses);
      setTotalAmount(expRes.data.total_amount);
      setHauls((haulRes.data || []).filter((h) => !h.is_virtual));
    } catch {} finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true);
    setShowUpload(false);

    const results = [];
    for (const file of files) {
      const b64 = await fileToBase64(file);
      try {
        const { data } = await expenseAPI.scan(b64);
        results.push({ ...data, confirmed: false });
      } catch {
        results.push({ vendor: file.name, date: "", amount: 0, category: "Other", description: "", receipt_path: "", confirmed: false, error: true });
      }
    }
    setPendingReview(results);
    setScanning(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const updatePending = (idx, field, value) => {
    setPendingReview((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const confirmAll = async () => {
    setScanning(true);
    for (const item of pendingReview) {
      if (item.error) continue;
      try {
        await expenseAPI.save({
          vendor: item.vendor,
          date: item.date,
          amount: parseFloat(item.amount) || 0,
          category: item.category,
          description: item.description,
          haul_id: item.haul_id || "",
          receipt_path: item.receipt_path,
        });
      } catch {}
    }
    setPendingReview([]);
    setScanning(false);
    await loadExpenses();
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete this expense?",
      message: "This expense will be removed from your records.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await expenseAPI.delete(id);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
    } catch {}
  };

  const handleUpdate = async (id, updates) => {
    try {
      await expenseAPI.update(id, updates);
      await loadExpenses();
    } catch {}
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Spinner size={32} weight="bold" className="animate-spin" />
      </div>
    );
  }

  // Review mode
  if (pendingReview.length > 0) {
    return (
      <div className="min-h-screen bg-white pb-24">
        <div className="sticky top-0 z-50 bg-white border-b-4 border-black py-4 px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setPendingReview([])} className="p-1"><ArrowLeft size={20} weight="bold" /></button>
            <h1 className="font-heading text-xl font-black tracking-tighter uppercase">Review Receipts</h1>
          </div>
          <span className="font-body text-xs text-neutral-500">{pendingReview.length} receipt{pendingReview.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="p-4 space-y-4">
          {pendingReview.map((item, idx) => (
            <div key={idx} className="border-2 border-black" data-testid={`review-receipt-${idx}`}>
              {item.receipt_path && (
                <div className="border-b border-black bg-neutral-50 p-2">
                  <img
                    src={`/api/images/file/${item.receipt_path}`}
                    alt="Receipt"
                    className="w-full object-contain max-h-40"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-3 space-y-2">
                {item.error && (
                  <div className="font-body text-xs text-red-600 bg-red-50 p-2">Scan failed — fill in manually</div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Vendor</label>
                    <input
                      type="text"
                      value={item.vendor}
                      onChange={(e) => updatePending(idx, "vendor", e.target.value)}
                      className="w-full p-1.5 border-2 border-black font-body text-sm"
                      data-testid={`review-vendor-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Date</label>
                    <input
                      type="date"
                      value={item.date}
                      onChange={(e) => updatePending(idx, "date", e.target.value)}
                      className="w-full p-1.5 border-2 border-black font-body text-sm"
                      data-testid={`review-date-${idx}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Amount</label>
                    <div className="flex items-center">
                      <span className="font-body text-sm font-bold mr-1">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={item.amount}
                        onChange={(e) => updatePending(idx, "amount", e.target.value)}
                        className="w-full p-1.5 border-2 border-black font-body text-sm"
                        data-testid={`review-amount-${idx}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Category</label>
                    <select
                      value={item.category}
                      onChange={(e) => updatePending(idx, "category", e.target.value)}
                      className="w-full p-1.5 border-2 border-black font-body text-sm bg-white"
                      data-testid={`review-category-${idx}`}
                    >
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Description</label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updatePending(idx, "description", e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm"
                    placeholder="What was purchased"
                    data-testid={`review-desc-${idx}`}
                  />
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Link to Haul (optional)</label>
                  <select
                    value={item.haul_id || ""}
                    onChange={(e) => updatePending(idx, "haul_id", e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm bg-white"
                    data-testid={`review-haul-${idx}`}
                  >
                    <option value="">General Expense</option>
                    {hauls.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="fixed bottom-20 left-0 right-0 p-4 bg-white border-t-4 border-black z-50">
          <button
            onClick={confirmAll}
            disabled={scanning}
            className="w-full p-3 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="confirm-all-btn"
          >
            {scanning ? <><Spinner size={16} className="animate-spin" /> Saving...</> : <><Check size={16} weight="bold" /> Save All Expenses</>}
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white">
        <FinanceTabs />
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200">
          <h1 className="font-heading text-xl font-black tracking-tighter uppercase">Expenses</h1>
          <button
            onClick={() => setShowUpload(true)}
            className="p-2 bg-black text-white border-2 border-black active:translate-y-[1px]"
            data-testid="add-expense-btn"
          >
            <Plus size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Total bar */}
      {expenses.length > 0 && (
        <div className="flex items-center justify-between p-3 border-b-2 border-black bg-neutral-50">
          <span className="font-body text-xs font-semibold uppercase text-neutral-500">
            {expenses.length} expense{expenses.length !== 1 ? "s" : ""}
          </span>
          <span className="font-heading text-lg font-black">${totalAmount.toFixed(2)}</span>
        </div>
      )}

      {/* Category filter */}
      <div className="p-3 border-b border-neutral-200 flex items-center gap-2 overflow-x-auto">
        <FunnelSimple size={14} weight="bold" className="text-neutral-400 shrink-0" />
        <button
          onClick={() => setFilterCat("")}
          className={`px-2 py-1 font-body text-xs font-semibold whitespace-nowrap border ${!filterCat ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600"}`}
        >
          All
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(filterCat === c ? "" : c)}
            className={`px-2 py-1 font-body text-xs font-semibold whitespace-nowrap border ${filterCat === c ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Scanning indicator */}
      {scanning && (
        <div className="p-4 flex items-center justify-center gap-2 bg-amber-50 border-b border-amber-200">
          <Spinner size={16} weight="bold" className="animate-spin text-amber-600" />
          <span className="font-body text-sm font-semibold text-amber-700">Scanning receipts...</span>
        </div>
      )}

      {/* Expense list */}
      {expenses.length === 0 ? (
        <div className="p-8 text-center" data-testid="expenses-empty">
          <Receipt size={48} weight="bold" className="mx-auto text-neutral-300 mb-4" />
          <div className="font-heading text-xl font-bold uppercase mb-2">No Expenses Yet</div>
          <p className="font-body text-sm text-neutral-500 mb-4">
            Snap photos of receipts for supplies, shipping materials, etc.
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black"
          >
            Add Expense
          </button>
        </div>
      ) : (
        <div>
          {expenses.map((exp) => (
            <ExpenseRow
              key={exp.id}
              expense={exp}
              hauls={hauls}
              isEditing={editingId === exp.id}
              onEdit={() => setEditingId(exp.id)}
              onCancelEdit={() => setEditingId(null)}
              onUpdate={(updates) => handleUpdate(exp.id, updates)}
              onDelete={() => handleDelete(exp.id)}
            />
          ))}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0">
          <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-sm sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between p-4 border-b-2 border-black">
              <h2 className="font-heading text-base font-black uppercase">Add Expenses</h2>
              <button onClick={() => setShowUpload(false)} className="p-1"><X size={18} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="font-body text-sm text-neutral-600">
                Upload one or more receipt photos. AI will extract vendor, amount, and category automatically.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFiles}
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full p-4 border-2 border-dashed border-black font-heading font-bold uppercase text-sm flex items-center justify-center gap-2 hover:bg-neutral-50"
                data-testid="upload-receipts-btn"
              >
                <Upload size={20} weight="bold" /> Upload Receipt Photos
              </button>
              <button
                onClick={() => {
                  if (fileRef.current) {
                    fileRef.current.setAttribute("capture", "environment");
                    fileRef.current.click();
                    fileRef.current.removeAttribute("capture");
                  }
                }}
                className="w-full p-4 border-2 border-black bg-black text-white font-heading font-bold uppercase text-sm flex items-center justify-center gap-2"
                data-testid="camera-receipt-btn"
              >
                <Camera size={20} weight="bold" /> Take Photo
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function ExpenseRow({ expense, hauls, isEditing, onEdit, onCancelEdit, onUpdate, onDelete }) {
  const [form, setForm] = useState({ ...expense });

  const catColor = {
    "Shipping Supplies": "bg-blue-100 text-blue-700",
    "Office Supplies": "bg-purple-100 text-purple-700",
    "Packaging": "bg-amber-100 text-amber-700",
    "Equipment": "bg-red-100 text-red-700",
    "Software/Subscriptions": "bg-green-100 text-green-700",
    "Gas/Mileage": "bg-orange-100 text-orange-700",
    "Sourcing Supplies": "bg-teal-100 text-teal-700",
    "Other": "bg-neutral-100 text-neutral-700",
  }[expense.category] || "bg-neutral-100 text-neutral-700";

  if (isEditing) {
    return (
      <div className="border-b-2 border-black p-3 space-y-2 bg-amber-50" data-testid={`expense-edit-${expense.id}`}>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Vendor</label>
            <input type="text" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              className="w-full p-1.5 border-2 border-black font-body text-sm" />
          </div>
          <div>
            <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Amount</label>
            <input type="text" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full p-1.5 border-2 border-black font-body text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Date</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full p-1.5 border-2 border-black font-body text-sm" />
          </div>
          <div>
            <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full p-1.5 border-2 border-black font-body text-sm bg-white">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Description</label>
          <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full p-1.5 border-2 border-black font-body text-sm" />
        </div>
        <div>
          <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Link to Haul</label>
          <select value={form.haul_id || ""} onChange={(e) => setForm({ ...form, haul_id: e.target.value })}
            className="w-full p-1.5 border-2 border-black font-body text-sm bg-white">
            <option value="">General Expense</option>
            {hauls.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onUpdate({ vendor: form.vendor, date: form.date, amount: parseFloat(form.amount) || 0, category: form.category, description: form.description, haul_id: form.haul_id || "" })}
            className="flex-1 p-2 bg-black text-white font-heading text-xs font-bold uppercase"
          >Save</button>
          <button onClick={onCancelEdit} className="p-2 border-2 border-black font-heading text-xs font-bold uppercase">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-neutral-200 p-3 flex items-center gap-3" data-testid={`expense-${expense.id}`}>
      {expense.receipt_path ? (
        <div className="w-10 h-10 border border-black shrink-0 overflow-hidden">
          <img src={`/api/images/file/${expense.receipt_path}`} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-10 h-10 border border-black shrink-0 flex items-center justify-center bg-neutral-50">
          <Receipt size={16} weight="bold" className="text-neutral-300" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-body text-sm font-semibold truncate">{expense.vendor || "Unknown"}</span>
          <span className={`px-1.5 py-0.5 font-body text-[9px] font-bold uppercase ${catColor}`}>
            {expense.category}
          </span>
        </div>
        <div className="font-body text-xs text-neutral-500 mt-0.5">
          {expense.date && <span>{expense.date}</span>}
          {expense.description && <span className="ml-2">{expense.description}</span>}
        </div>
      </div>
      <div className="font-heading text-sm font-bold shrink-0">${(expense.amount || 0).toFixed(2)}</div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onEdit} className="p-1 text-neutral-400 hover:text-black" data-testid={`edit-expense-${expense.id}`}>
          <PencilSimple size={14} weight="bold" />
        </button>
        <button onClick={onDelete} className="p-1 text-neutral-400 hover:text-red-600" data-testid={`delete-expense-${expense.id}`}>
          <Trash size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}
